import { Refine, Authenticated } from '@refinedev/core';
import routerProvider, { NavigateToResource, CatchAllNavigate } from '@refinedev/react-router-v6';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { ConfigProvider, App as AntdApp } from 'antd';
import frFR from 'antd/locale/fr_FR';

import { authProvider } from '@/authProvider/authProvider';
import { dataProvider } from '@/dataProvider/dataProvider';
import { accessControlProvider } from '@/accessControl/accessControlProvider';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage } from '@/pages/login/LoginPage';

// Growth
import GrowthDashboardPage from '@/pages/growth/GrowthDashboardPage';
import MarketingDashboardPage from '@/pages/growth/MarketingDashboardPage';
import ContentFactoryPage from '@/pages/growth/ContentFactoryPage';
import SalesIntelligencePage from '@/pages/growth/SalesIntelligencePage';
import KnowledgeExplorerPage from '@/pages/growth/KnowledgeExplorerPage';
import ContentCalendarPage from '@/pages/growth/ContentCalendarPage';
import BlogManagerPage from '@/pages/growth/BlogManagerPage';
import CampaignsPage from '@/pages/growth/CampaignsPage';
import ReferralDashboardPage from '@/pages/growth/ReferralDashboardPage';
import RoadmapPage from '@/pages/growth/RoadmapPage';

// Support
import FeedbackInboxPage from '@/pages/support/FeedbackInboxPage';
import UserListPage from '@/pages/support/UserListPage';
import UserDetailPage from '@/pages/support/UserDetailPage';

// Ops
import OpsDashboardPage from '@/pages/ops/OpsDashboardPage';
import OrganizationListPage from '@/pages/ops/OrganizationListPage';
import TokenWatcherPage from '@/pages/ops/TokenWatcherPage';
import SubscriptionsDashboardPage from '@/pages/ops/SubscriptionsDashboardPage';
import ByokManagerPage from '@/pages/ops/ByokManagerPage';
import RefundsPage from '@/pages/ops/billing/RefundsPage';
import CouponsPage from '@/pages/ops/billing/CouponsPage';
import AiConfigPage from '@/pages/ops/AiConfigPage';
import PlanGatesPage from '@/pages/ops/PlanGatesPage';
import {
  DedicatedServerListPage,
  DedicatedServerDetailPage,
} from '@/pages/ops/dedicated-servers';
import {
  PricingListPage,
  PricingCreatePage,
  PricingEditPage,
  PricingHistoryPage,
  RevenueSimulatorPage,
} from '@/pages/ops/pricing';

// Tech
import TechDashboardPage from '@/pages/tech/TechDashboardPage';

// Learning
import LearningHubPage from '@/pages/learning/LearningHubPage';
import CoursePage from '@/pages/learning/CoursePage';
import LessonPage from '@/pages/learning/LessonPage';

const THEME = {
  token: {
    colorPrimary: '#1325ec',
    borderRadius: 8,
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
};

function App() {
  return (
    <BrowserRouter>
      <ConfigProvider theme={THEME} locale={frFR}>
        <AntdApp>
          <Refine
            routerProvider={routerProvider}
            authProvider={authProvider}
            dataProvider={dataProvider}
            accessControlProvider={accessControlProvider}
            resources={[
              {
                name: 'growth',
                list: '/growth',
                meta: { label: 'Business', icon: '🚀' },
              },
              {
                name: 'support',
                list: '/support',
                meta: { label: 'Support', icon: '🎧' },
              },
              {
                name: 'admin/users',
                list: '/support/users',
                show: '/support/users/:id',
                meta: { label: 'Utilisateurs', parent: 'support', icon: '👤' },
              },
              {
                name: 'admin/feedbacks',
                list: '/support',
                meta: { label: 'Feedbacks', parent: 'support', icon: '💬', hide: true },
              },
              {
                name: 'ops',
                list: '/ops',
                meta: { label: 'Opérations', icon: '💳' },
              },
              {
                name: 'admin/organizations',
                list: '/ops/organizations',
                meta: { label: 'Organisations', parent: 'ops', icon: '🏢' },
              },
              {
                name: 'admin/billing/subscriptions',
                list: '/ops/subscriptions',
                meta: { label: 'Abonnements', parent: 'ops', icon: '💳' },
              },
              {
                name: 'admin/subscription/quotas',
                list: '/ops/token-watcher',
                meta: { label: 'Token Watcher', parent: 'ops' },
              },
              {
                name: 'admin/dedicated-servers',
                list: '/ops/dedicated-servers',
                show: '/ops/dedicated-servers/:id',
                meta: {
                  label: 'Serveurs dédiés',
                  parent: 'ops',
                  icon: '🖥️',
                  canDelete: false,
                },
              },
              {
                name: 'admin/pricing',
                list: '/ops/pricing',
                create: '/ops/pricing/new',
                edit: '/ops/pricing/:id/edit',
                meta: {
                  label: 'Pricing',
                  parent: 'ops',
                  canDelete: true,
                },
              },
              {
                name: 'admin/billing/refunds',
                list: '/ops/billing/refunds',
                meta: { label: 'Remboursements', parent: 'ops' },
              },
              {
                name: 'admin/billing/coupons',
                list: '/ops/billing/coupons',
                meta: { label: 'Coupons', parent: 'ops' },
              },
              {
                name: 'admin/ai/providers',
                list: '/ops/ai-config',
                meta: { label: 'Config IA', parent: 'ops' },
              },
              {
                name: 'admin/plan-gates',
                list: '/ops/plan-gates',
                meta: { label: 'Plan Gates', parent: 'ops' },
              },
              {
                name: 'growth/roadmap',
                list: '/growth/roadmap',
                meta: { label: 'Roadmap', parent: 'growth', hide: true },
              },
              {
                name: 'tech',
                list: '/tech',
                meta: { label: 'Tech', icon: '⚙️' },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
            }}
          >
            <Routes>
              {/* Public routes */}
              <Route
                element={
                  <Authenticated key="auth-login" fallback={<Outlet />}>
                    <NavigateToResource resource="growth" />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<LoginPage />} />
              </Route>

              {/* Protected routes */}
              <Route
                element={
                  <Authenticated key="auth-pages" fallback={<CatchAllNavigate to="/login" />}>
                    <AppLayout>
                      <Outlet />
                    </AppLayout>
                  </Authenticated>
                }
              >
                <Route index element={<NavigateToResource resource="growth" />} />

                {/* Growth */}
                <Route path="/growth" element={<GrowthDashboardPage />} />
                <Route path="/growth/marketing-dashboard" element={<MarketingDashboardPage />} />
                <Route path="/growth/content-factory" element={<ContentFactoryPage />} />
                <Route path="/growth/sales-intelligence" element={<SalesIntelligencePage />} />
                <Route path="/growth/knowledge-explorer" element={<KnowledgeExplorerPage />} />
                <Route path="/growth/content-calendar" element={<ContentCalendarPage />} />
                <Route path="/growth/blog-manager" element={<BlogManagerPage />} />
                <Route path="/growth/campaigns" element={<CampaignsPage />} />
                <Route path="/growth/referral-dashboard" element={<ReferralDashboardPage />} />
                <Route path="/growth/roadmap" element={<RoadmapPage />} />

                {/* Support */}
                <Route path="/support" element={<FeedbackInboxPage />} />
                <Route path="/support/users" element={<UserListPage />} />
                <Route path="/support/users/:id" element={<UserDetailPage />} />

                {/* Ops */}
                <Route path="/ops" element={<OpsDashboardPage />} />
                <Route path="/ops/organizations" element={<OrganizationListPage />} />
                <Route path="/ops/subscriptions" element={<SubscriptionsDashboardPage />} />
                <Route path="/ops/token-watcher" element={<TokenWatcherPage />} />
                <Route path="/ops/organizations/:id/byok" element={<ByokManagerPage />} />
                <Route path="/ops/dedicated-servers" element={<DedicatedServerListPage />} />
                <Route path="/ops/dedicated-servers/:id" element={<DedicatedServerDetailPage />} />

                {/* Billing */}
                <Route path="/ops/billing/refunds" element={<RefundsPage />} />
                <Route path="/ops/billing/coupons" element={<CouponsPage />} />

                {/* Ops config */}
                <Route path="/ops/ai-config" element={<AiConfigPage />} />
                <Route path="/ops/plan-gates" element={<PlanGatesPage />} />

                {/* Pricing (Sprint 5 — Agent O) */}
                <Route path="/ops/pricing" element={<PricingListPage />} />
                <Route path="/ops/pricing/new" element={<PricingCreatePage />} />
                <Route path="/ops/pricing/simulator" element={<RevenueSimulatorPage />} />
                <Route path="/ops/pricing/history" element={<PricingHistoryPage />} />
                <Route path="/ops/pricing/:id/edit" element={<PricingEditPage />} />

                {/* Tech */}
                <Route path="/tech" element={<TechDashboardPage />} />

                {/* Learning */}
                <Route path="/learning" element={<LearningHubPage />} />
                <Route path="/learning/:courseSlug" element={<CoursePage />} />
                <Route path="/learning/:courseSlug/:lessonSlug" element={<LessonPage />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<NavigateToResource resource="growth" />} />
            </Routes>
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}

export default App;
